import { useEffect, useRef, useState } from "react";
import secureLocalStorage from "react-secure-storage";
import { Button } from "../../../components/Button";
import MuiButton from "@mui/material/Button";
import addCircleImage from "./../../../assets/_AddCircleIcon_.svg";
import { IArea } from "../../../@types/area.type";
import vectorIcon from "../../../assets/_VectorIcon_.svg";
import styles from "./styles.module.scss";
import usePermission from "../../../hooks/usePermission";
import { FeatureStatic } from "../../../@types/statics/feature.static";
import { useNavigate } from "react-router-dom";
import { useSyncActive } from "../../../utils/useSyncActive";

import { AgGridReact } from "ag-grid-react";
import { ColDef, FilterChangedEvent, IGetRowsParams } from "ag-grid-community";
import { themeGrid } from "../../../themeGrid";
import { localeText } from "../../../localeTextFromGrid";
import { searchAreas } from "../../../services/api/areas/AreasService";
import { IApiResponse } from "../../../@types/api-response.type";
import { IGrid } from "../../../@types/grid.type";
import { IAreaFilterRequest } from "../../../@types/area-filter-request.type";

const columnsDefault: ColDef[] = [
  {
    field: "id",
    headerName: "ID",
    hide: true,
  },
  {
    field: "areaName",
    headerName: "Nome",
    minWidth: 300,
    flex: 2,
    cellRenderer: (params) => <>{params.value}</>,
  },
  {
    field: "managerName",
    headerName: "Responsável",
    flex: 3,
    minWidth: 300,
  },
  {
    field: "description",
    headerName: "Descrição",
    minWidth: 250,
    flex: 3,
    sortable: false
  },
];

export const Areas: React.FC = () => {
  const [columns, setColumns] = useState(columnsDefault);
  const gridRef = useRef<AgGridReact<any>>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const isSyncActive = useSyncActive();

  const permissions = usePermission(FeatureStatic.areas.id);

  const navigate = useNavigate();

  const getFromCache = (key: string) => {
    const data = secureLocalStorage.getItem(key);
    return data ? JSON.parse(data as string) : null;
  };

  const [cachedFilters, setCachedFilters] = useState(
    getFromCache("agGridFiltersAreas")
  );
  const [cachedSort, setCachedSort] = useState(getFromCache("agGridSortAreas"));

  const saveToCache = (key: string, data: any) => {
    secureLocalStorage.setItem(key, JSON.stringify(data));
  };

  const onFilterChanged = (params: FilterChangedEvent) => {
    const filters = params.api.getFilterModel(); // Obtém o modelo de filtro
    saveToCache("agGridFiltersAreas", filters); // Salva no cache
  };

  const onSortChanged = () => {
    const colState = gridRef.current!.api.getColumnState();
    const sortState = colState
      .filter(function (s) {
        return s.sort != null;
      })
      .map(function (s) {
        return { colId: s.colId, sort: s.sort, sortIndex: s.sortIndex };
      });

    saveToCache("agGridSortAreas", sortState);
  };

  useEffect(() => {
    if (!gridRef.current) return;

    const gridApi = gridRef.current.api;

    if (gridApi) {
      // Restaurar filtros
      if (cachedFilters) {
        gridApi.setFilterModel(cachedFilters);
      }

      // Restaurar ordenação
      if (cachedSort) {
        gridApi.applyColumnState({
          state: cachedSort,
          defaultState: { sort: null },
        });
      }
    }
  }, [cachedFilters, cachedSort, columns]);

  useEffect(() => {
    if (!permissions.canView()) {
      navigate("/unauthorized");
      return;
    }
  }, []);

  function navigateToUpdatePage(id: string) {
    navigate(`update/${id}`);
  }

  const getDataSource = () => ({
    getRows: async (params: IGetRowsParams) => {
      console.log(params);

      const sortModel = params.sortModel;
      const filterModel = params.filterModel;

      const filter =
        {
          skip: params.startRow, // Linha inicial para paginação
          take: params.endRow - params.startRow // Quantidade de linhas
        } as IAreaFilterRequest;

      if (sortModel.length > 0) {
        filter.orderByField = sortModel[0].colId;
        filter.orderByDirection = sortModel[0].sort == "asc" ? 0 : 1;
      }

      if (filterModel.name) {
        filter.name = filterModel.name.filter;
      }

      if (filterModel.managerName) {
        filter.managerName = filterModel.managerName.filter;
      }

      if (filterModel.description) {
        filter.description = filterModel.description.filter;
      }

      try {
        const response = (await searchAreas(filter)) as IApiResponse<
          IGrid<IArea>
        >;

        params.successCallback(
          response.result.items,
          response.result.totalRows
        );
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
        params.failCallback(); // Notifica falha no carregamento
      }
    },
  });

  return (
    <>
      <main className={styles.areasStyle}>
        <div className={styles.back}>
          <MuiButton
            className={styles.buttonMui}
            onClick={() => navigate("/configurations")}
          >
            {vectorIcon && <img src={vectorIcon} />}
            Voltar a tela anterior
          </MuiButton>
        </div>
        <div className={styles.titleAndBtnCreate}>
          <h1>Áreas</h1>
          <div className={styles.btnsFilterAndCreate}>
            {permissions.canEdit() && !isSyncActive && (
              <Button
                iconUrl={addCircleImage}
                onClick={() => {
                  navigate("/configurations/areas/create");
                }}
                content="Nova Área"
                className={styles.button}
              />
            )}
          </div>
        </div>
        <div className={`${styles.dataGridContainer}`}>
          <AgGridReact
            ref={gridRef}
            theme={themeGrid}
            localeText={localeText}
            loading={loading}
            columnDefs={columns}
            overlayNoRowsTemplate="<span style='color: gray;'>Sem registros encontrados</span>"
            defaultColDef={{
              sortable: true,
              editable: false,
              filter: true,
              floatingFilter: true,
            }}
            suppressCellFocus={true}
            rowStyle={{ cursor: isSyncActive ? "default" : "pointer" }}
            onRowClicked={(row) =>
              !isSyncActive && permissions.canEdit()
                ? navigateToUpdatePage(row.data.id)
                : undefined
            }
            cacheBlockSize={10}
            onFilterChanged={onFilterChanged}
            onSortChanged={onSortChanged}
            rowModelType="infinite"
            datasource={getDataSource()}
            animateRows={true}
          />
        </div>
      </main>
    </>
  );
};
